import React, { useContext } from 'react';
import styled, { css } from 'styled-components';

import ReportCanvas, { getCanvasMode } from '../ReportCanvas';
import VisualisationsGate from '../VisualisationsGate';
import MetricsGate from '../MetricsGate';
import StudioBlockEditorProvider from '../../contextProviders/StudioBlockEditorProvider';
import Row from '../Common/Row';
import ReportEditingButtons from '../ReportEditingButtons';
import ReportMenu from '../ReportMenu';
import ConfigureReportGadgetModal from '../ConfigureReportGadgetModal';
import ResetReportButton from './ResetReportButton';
import FilterPlates from '../FilterPlates';
import ComparisonSelector from './ComparisonSelector';
import ExportReportButton from '../ExportReportButton';
import DateInput from '../DateInput';
import WeekStartsOnOverrideSelector from './WeekStartsOnOverrideSelector';
import {
  CANVAS_ID,
  EXCLUDE_HEIGHT,
  MODAL_MARGIN,
  TOP_BAR_HEIGHT,
} from '../../constants';

import ResourceTopBar, { ResourceFavouriteButton } from '../ResourceTopBar';
import Typography from 'kingpin/atoms/Typography';
import ReportInfoIcon from '../ReportInfoIcon';
import PermissionGates from '../PermissionGates';
import PERMISSIONS from '../../permissionDefinitions';
import PublishMetricsButton2 from './PublishMetricsButton';
import ShareContentButton from '../ShareContentButton';
import OpenInsightsButton from '../InsightsChat/OpenInsightsButton';
import EntityFilterToggle from '../EntityFilterToggle';
import PortalAccountsGate from '../PortalAccountsGate';
import DefaultFiltersToggle from '../DefaultFiltersToggle';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import { UIVersion } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider/types';
import V3ContentBackButton from '../../screens/V3/Content/V3ContentBackButton';
import V3PublishOrShareButton from '../../screens/V3/Content/V3PublishOrShareButton';
import V3PrivateIndicator from '../../screens/V3/Content/V3PrivateIndicator';
import AiGeneratedBadge from '../AiGeneratedBadge';
import ReportContext from '../../contexts/ReportContext';
import ReportQuickFiltersDisplay from './QuickFilters/ReportQuickFiltersDisplay';
import PinnedFilterPlates from './PinnedFilters/PinnedFilterPlates';
import usePinnedFilterSlots from './PinnedFilters/usePinnedFilterSlots';

import EntityAppUsageAlert from './EntityAppUsageAlert';

const FiltersRow = styled(Row)`
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 24px;
  top: 0px;
  z-index: 11;
  margin-left: 0px;
  position: sticky;
  margin-bottom: 1px;
  background-color: var(--color-bg-primary);
`;

const LeftGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
`;

const PopupCss = css`
  height: calc(100vh - ${MODAL_MARGIN * 2}px);
  max-height: calc(100vh - ${MODAL_MARGIN * 2}px);
`;

const ValidationReportCss = css`
  height: calc(100vh - ${65}px);
  max-height: calc(100vh - ${65}px);
`;

const TargetAppReportCss = css`
  height: calc(100vh - ${TOP_BAR_HEIGHT * 2}px);
  max-height: calc(100vh - ${TOP_BAR_HEIGHT * 2}px);
`;

const V3ReportCss = css`
  height: unset;
  flex: 1;
`;

const Content = styled.div<{
  isPopup?: boolean;
  isValidationReport?: boolean;
  isTargetsApp?: boolean;
  uiVersion: UIVersion;
}>`
  width: 100%;
  position: relative;
  height: 100vh;
  overflow-y: scroll;
  ${(props) => props.isValidationReport && ValidationReportCss}
  ${(props) => props.isTargetsApp && TargetAppReportCss}
  ${(props) => props.isPopup && PopupCss}
  ${(props) => props.uiVersion === 'v3' && V3ReportCss}
`;

const CONTEXT_MENU_PERMISSIONS = [
  PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT,
  PERMISSIONS.REPORT_MANAGEMENT.ADD_REMOVE_VISUALISATIONS,
  PERMISSIONS.REPORT_MANAGEMENT.DELETE,
  PERMISSIONS.REPORT_MANAGEMENT.COPY,
  PERMISSIONS.CONTENT_ACCESS.SET_ANY_AVAILABLE_TO_USERS_OR_PUBLIC,
];

const AdminStuff = ({
  isEditing,
  startEditing,
  exportReport,
  saveReportAs,
  onDelete,
  hideMenu,
  onAddVisualization,
  setCurrentCanvas,
  canvasMode,
}: {
  isEditing: boolean;
  startEditing: () => void;
  exportReport: (
    userIds: string[],
    notes: string,
    senderIdOverride?: string,
  ) => Promise<void>;
  saveReportAs: (name: string, labelIds: string[]) => Promise<any>;
  saveEditAs: (name: string, labelIds: string[]) => Promise<any>;
  onDelete: () => void;
  hideMenu?: boolean;
  onAddVisualization?: () => void;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  canvasMode: CanvasMode;
}) => {
  if (isEditing) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      {!isEditing && (
        <Row>
          <ExportReportButton exportReport={exportReport} />

          {!hideMenu && (
            <PermissionGates.HasAny
              requiredPermissions={CONTEXT_MENU_PERMISSIONS}
            >
              <>
                <div style={{ marginRight: 12 }} />
                <ReportMenu
                  startEditing={startEditing}
                  saveReportAs={saveReportAs}
                  onDelete={onDelete}
                  onAddVisualization={onAddVisualization}
                  setCurrentCanvas={setCurrentCanvas}
                  canvasMode={canvasMode}
                />
              </>
            </PermissionGates.HasAny>
          )}
        </Row>
      )}
    </div>
  );
};
const Report = ({
  report,
  reportId,
  currentCanvas,
  setCurrentCanvas,
  isEditing,
  onEditSave,
  onEditCancel,
  exportReport,
  isRenderComplete,
  startEditing,
  saveReportAs,
  saveEditAs,
  onDelete,
  hasLeftDefaultState,
  onAddVisualization,
  hideMenu,
  weekStartsOnOverride,
  setWeekStartsOnOverride,
  isMobile,
  isPopup,
  onScroll,
  isValidationReport,
  isWorkspace,
  isTargetsApp,
  isEntityApp,
  isEntityFilterEnabled,
  setIsEntityFilterEnabled,
  dataTypes,
  isConfigureOpen,
  closeConfigure,
  editingReportGadget,
  onReportGadgetSaved,
  onEditReportGadget,
}: {
  report: ReportType;
  reportId: string;
  currentCanvas: Canvas;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  isEditing: boolean;
  onEditSave: () => void;
  onEditCancel: () => void;
  exportReport: (
    userIds: string[],
    notes: string,
    senderIdOverride?: string,
  ) => Promise<void>;
  isRenderComplete: boolean;
  startEditing: () => void;
  saveReportAs: (name: string, labelIds: string[]) => Promise<any>;
  saveEditAs: (name: string, labelIds: string[]) => Promise<any>;
  onDelete: () => void;
  hasLeftDefaultState: boolean;
  onAddVisualization: () => void;
  hideMenu?: boolean;
  weekStartsOnOverride?: WeekStartsOn;
  setWeekStartsOnOverride: React.Dispatch<
    React.SetStateAction<WeekStartsOn | undefined>
  >;
  isMobile: boolean;
  isPopup?: boolean;
  onScroll: () => void;
  isValidationReport?: boolean;
  isWorkspace: boolean;
  isTargetsApp: boolean;
  isEntityApp: boolean;
  isEntityFilterEnabled: boolean;
  setIsEntityFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  dataTypes: string[];
  isConfigureOpen: boolean;
  closeConfigure: () => void;
  editingReportGadget?: ReportGadgets.ReportGadget;
  onReportGadgetSaved: (gadget: ReportGadgets.ReportGadget) => void;
  onEditReportGadget: (gadget: ReportGadgets.ReportGadget) => void;
}) => {
  const { uiVersion } = useContext(UIVersionContext);
  const { quickFilters } = useContext(ReportContext);
  const hasQuickFilters = quickFilters.length > 0;
  const { slots: pinnedFilterSlots, hiddenDrillDownIds } = usePinnedFilterSlots(
    { pins: report.pinnedFilterFields },
  );

  return (
    <StudioBlockEditorProvider contentType="report">
      <Content
        id="report"
        onScroll={onScroll}
        className="hiding-scrollbar"
        isPopup={isPopup}
        isValidationReport={isValidationReport}
        isTargetsApp={isTargetsApp}
        uiVersion={uiVersion}
        data-report-render-complete={isRenderComplete ? 'true' : undefined}
      >
        <div id={`${CANVAS_ID}-${reportId}`} style={{ position: 'relative' }}>
          {!isPopup && !isWorkspace && !isTargetsApp && !isEntityApp && (
            <ResourceTopBar
              isCanvas
              className={`${EXCLUDE_HEIGHT}-${reportId}`}
            >
              <Row
                centerAlign
                style={{ height: '100%', width: '100%' }}
                spaceBetween
              >
                <Row centerAlign style={{ height: '100%', gap: 8 }}>
                  <V3ContentBackButton />
                  <Typography.Header type="H3">
                    {report ? report.name : '...'}
                  </Typography.Header>
                  {report && (
                    <div style={{ display: 'flex' }}>
                      <ResourceFavouriteButton
                        type="report"
                        typeId={report.id}
                        name={report.name}
                      />
                    </div>
                  )}
                  {report && (
                    <div style={{ display: 'flex' }}>
                      <ReportInfoIcon report={report} />
                    </div>
                  )}
                  <V3PrivateIndicator />
                  {report?.isAIGenerated && <AiGeneratedBadge />}
                </Row>
                <Row centerAlign style={{ gap: 8 }}>
                  <OpenInsightsButton />
                  <PortalAccountsGate>
                    <ShareContentButton
                      typeId={report.id}
                      type={'report'}
                      contentName={report.name}
                    />
                  </PortalAccountsGate>
                  <V3PublishOrShareButton />
                  {isValidationReport && <PublishMetricsButton2 />}
                </Row>
              </Row>
            </ResourceTopBar>
          )}

          {!isMobile && isEditing && (
            <>
              <Row
                centerAlign
                spaceBetween
                style={{
                  padding: isMobile ? 0 : '8px 24px 2px',
                }}
                className={`${EXCLUDE_HEIGHT}-${reportId}`}
              >
                <EntityAppUsageAlert />
                <div style={{ marginLeft: 'auto' }}>
                  <ReportEditingButtons
                    onSave={onEditSave}
                    onCancel={onEditCancel}
                    saveEditAs={saveEditAs}
                    isEditing={isEditing}
                  />
                </div>
              </Row>
            </>
          )}

          <FiltersRow
            style={{
              borderBottom: `1px solid var(--color-border-secondary)`,
            }}
            className={`${EXCLUDE_HEIGHT}-${reportId}`}
            id={'report-filters'}
          >
            <LeftGroup>
              <DateInput isReport />
              {!isEditing && hasQuickFilters && <ReportQuickFiltersDisplay />}
              {isEditing && <FilterPlates isScope />}
              {/* A popup's run-time filters (its gadget's and the value
                  clicked) can each be removed. Its scope applies unseen. */}
              {!isEditing && (
                <FilterPlates
                  isReport
                  isEveryFilterInteractive={isPopup}
                  hiddenDrillDownIds={hiddenDrillDownIds}
                  leadingChildren={
                    <PinnedFilterPlates slots={pinnedFilterSlots} />
                  }
                >
                  {hasLeftDefaultState && <ResetReportButton />}
                </FilterPlates>
              )}
              {isEditing && (
                <WeekStartsOnOverrideSelector
                  weekStartsOnOverride={weekStartsOnOverride}
                  setWeekStartsOnOverride={setWeekStartsOnOverride}
                />
              )}
              {isEditing && (
                <>
                  <EntityFilterToggle
                    isEntityFilterEnabled={isEntityFilterEnabled}
                    setIsEntityFilterEnabled={setIsEntityFilterEnabled}
                  />
                  <DefaultFiltersToggle relevantDataTypes={dataTypes} />
                </>
              )}
            </LeftGroup>
            <RightGroup>
              <ComparisonSelector isReport />
              {!isMobile && !isPopup && (
                <AdminStuff
                  isEditing={isEditing}
                  saveReportAs={saveReportAs}
                  saveEditAs={saveEditAs}
                  exportReport={exportReport}
                  startEditing={startEditing}
                  onDelete={onDelete}
                  hideMenu={hideMenu}
                  onAddVisualization={onAddVisualization}
                  setCurrentCanvas={setCurrentCanvas}
                  canvasMode={getCanvasMode({ isMobile })}
                />
              )}
            </RightGroup>
          </FiltersRow>
          <div style={{ position: 'relative' }}>
            {/*
             * Gate only the canvas — not the title bar / filters above — so the
             * report chrome stays visible while visualisation + metric
             * definitions load, and only the canvas area shows the loader.
             */}
            <VisualisationsGate content={report} contentType="Report">
              <MetricsGate content={report} contentType="Report">
                <ReportCanvas
                  isEditing={isEditing}
                  currentCanvas={currentCanvas}
                  setCurrentCanvas={setCurrentCanvas}
                  startEditing={startEditing}
                />
              </MetricsGate>
            </VisualisationsGate>
          </div>
        </div>
      </Content>
      <ConfigureReportGadgetModal
        isOpen={isConfigureOpen}
        close={closeConfigure}
        editingGadget={editingReportGadget}
        onSaved={onReportGadgetSaved}
      />
    </StudioBlockEditorProvider>
  );
};

export default Report;
